import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import GigRequestConversationModal from "../../../components/gigs/GigRequestConversationModal";
import TeenRequestedGigsFilters from "../../../components/teen/gigs/TeenRequestedGigsFilters";
import TeenRequestedGigDetail from "../../../components/teen/gigs/TeenRequestedGigDetail";
import TeenRequestedGigsTable from "../../../components/teen/gigs/TeenRequestedGigsTable";
import Pagination from "../../../components/gigs/Pagination";
import PageBackButton from "../../../components/ui/Page/PageBackButton";
import { ScheduleProposalForm } from "../../../components/gigs/ScheduleProposalModal";
import Button from "../../../components/ui/Button/Button";
import Modal from "../../../components/ui/Modal/Modal";
import Toast, { type ToastState } from "../../../components/ui/Toast/Toast";
import useTeenGuardianRequirement from "../../../hooks/useTeenGuardianRequirement";
import {
  ACTIVE_REQUEST_STATUSES,
  PAST_REQUEST_STATUSES,
  type RequestViewFilter,
} from "../../../components/teen/gigs/constants";
import {
  buildRequestSummary,
  formatGigPayment,
  getEmptyStateCopy,
  isAwaitingTeenScheduleConfirmation,
} from "../../../components/teen/gigs/utils";
import {
  captureGigAppliedAgain,
  captureGigRequestWithdrawn,
  captureTeenScheduleProposalSubmitted,
  captureTeenSuggestedTimeConfirmed,
} from "../../../lib/analytics";
import { gigsApi } from "../../../lib/gigs";
import type { ScheduleProposalRequest, TeenGigRequestListItemResponse } from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import {
  formatDateTimeInGigTimeZone,
  resolveGigTimeZone,
  toDateTimeLocalInputInTimeZone,
  toMinutePrecision,
  toZonedIsoString,
} from "../../../utils/DateUtils";
import { buildGuardianSetupHref } from "../../../utils/GuardianSetupUtils";
import { stripeApi } from "../../../lib/stripe";
import type { StripePayoutProfileResponse } from "../../../types/stripeTypes";

const PAGE_SIZE = 10;
const PRE_ACCEPTANCE_CONTACT_INFO_NOTICE =
  "As noted in our Terms and for safety reasons, do not share phone numbers, email addresses, social handles, or other personal contact information before the gig is accepted.";

const isModalSection = (value: string | null): value is "details" | "schedule" =>
  value === "details" || value === "schedule";

type PayoutSetupActionKind = "settings" | "stripe" | "retry" | "close";

type PayoutSetupModalState = {
  statusLabel: string;
  title: string;
  description: string;
  actionLabel: string;
  actionKind: PayoutSetupActionKind;
};

const buildPayoutSetupModalState = (
  profile: StripePayoutProfileResponse | null,
  isLoading: boolean,
  error: string | null,
  gigAmountLabel: string,
): PayoutSetupModalState => {
  if (isLoading) {
    return {
      statusLabel: "Checking setup",
      title: "Checking payout setup",
      description: `Checking what still needs to happen before the poster can fund this ${gigAmountLabel} gig.`,
      actionLabel: "Checking...",
      actionKind: "close",
    };
  }

  if (error) {
    return {
      statusLabel: "Could not check",
      title: "Payout status unavailable",
      description: error,
      actionLabel: "Try Again",
      actionKind: "retry",
    };
  }

  if (profile?.onboardingStatus === "READY" && profile.payoutsEnabled) {
    return {
      statusLabel: "Ready",
      title: "Payout setup is ready",
      description: `Your payout setup is ready for this ${gigAmountLabel} gig. The poster still needs to fund and fully confirm it.`,
      actionLabel: "Close",
      actionKind: "close",
    };
  }

  if (profile?.requiresGuardianSelection) {
    return {
      statusLabel: "Connect guardian",
      title: "Choose a payout guardian",
      description: `This gig pays ${gigAmountLabel}. Choose the parent or guardian who should handle Stripe payout setup before the poster can fund the gig.`,
      actionLabel: "Choose Guardian",
      actionKind: "settings",
    };
  }

  if (profile?.payoutOwnerType === "GUARDIAN") {
    const guardianLabel = profile.guardianName ?? "your parent or guardian";
    const waitingOnGuardian = Boolean(profile.guardianOnboardingEmailSentAt);
    return {
      statusLabel: waitingOnGuardian ? "Waiting on guardian" : "Guardian Stripe needed",
      title: waitingOnGuardian ? "Guardian setup in progress" : "Guardian-managed payout setup",
      description: waitingOnGuardian
        ? `This gig pays ${gigAmountLabel}. ${guardianLabel} still needs to complete Stripe payout setup before the poster can fund the gig.`
        : `This gig pays ${gigAmountLabel}. ${guardianLabel} needs to complete Stripe payout setup so the poster can fund the gig and your payout can be processed after completion.`,
      actionLabel: waitingOnGuardian ? "View Guardian Setup Status" : "Send Guardian Setup Link",
      actionKind: "settings",
    };
  }

  return {
    statusLabel: profile?.onboardingStatus === "ONBOARDING_REQUIRED" ? "Continue Stripe" : "Stripe needed",
    title: profile?.onboardingStatus === "ONBOARDING_REQUIRED" ? "Continue Stripe setup" : "Set up Stripe payouts",
    description: `This gig pays ${gigAmountLabel}. Set up your Stripe payout account so the poster can fund the gig and your payout can be processed after completion.`,
    actionLabel: profile?.onboardingStatus === "ONBOARDING_REQUIRED" ? "Continue Stripe Setup" : "Set Up Stripe",
    actionKind: "stripe",
  };
};

const TeenGigsPage = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gigs, setGigs] = useState<TeenGigRequestListItemResponse[]>([]);
  const [isLoadingGigs, setIsLoadingGigs] = useState(true);
  const [gigError, setGigError] = useState<string | null>(null);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [pendingScheduleRequestId, setPendingScheduleRequestId] = useState<string | null>(null);
  const [scheduleProposalError, setScheduleProposalError] = useState<string | null>(null);
  const [requestViewFilter, setRequestViewFilter] = useState<RequestViewFilter>("ACTIVE");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedDetailGig, setSelectedDetailGig] = useState<TeenGigRequestListItemResponse | null>(null);
  const [selectedConversationGig, setSelectedConversationGig] = useState<TeenGigRequestListItemResponse | null>(null);
  const [pendingWithdrawGig, setPendingWithdrawGig] = useState<TeenGigRequestListItemResponse | null>(null);
  const [pendingReapplyGig, setPendingReapplyGig] = useState<TeenGigRequestListItemResponse | null>(null);
  const [pendingReapplyRequestId, setPendingReapplyRequestId] = useState<string | null>(null);
  const [reapplyMessage, setReapplyMessage] = useState("");
  const [reapplyStartTime, setReapplyStartTime] = useState("");
  const [reapplyEndTime, setReapplyEndTime] = useState("");
  const [reapplyError, setReapplyError] = useState<string | null>(null);
  const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null);
  const [payoutSetupGig, setPayoutSetupGig] = useState<TeenGigRequestListItemResponse | null>(null);
  const [payoutProfile, setPayoutProfile] = useState<StripePayoutProfileResponse | null>(null);
  const [isLoadingPayoutProfile, setIsLoadingPayoutProfile] = useState(false);
  const [payoutSetupError, setPayoutSetupError] = useState<string | null>(null);
  const [isStartingPayoutOnboarding, setIsStartingPayoutOnboarding] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [isScheduleProposalComposerOpen, setIsScheduleProposalComposerOpen] = useState(false);
  const teenGuardianRequirement = useTeenGuardianRequirement();

  const loadTeenGigs = async () => {
    setIsLoadingGigs(true);
    setGigError(null);
    try {
      const requestedGigs = await gigsApi.listTeenRequestedGigs();
      setGigs(requestedGigs);
      return requestedGigs;
    } catch (requestError) {
      setGigError(apiErrorToMessage(requestError, "Failed to load your gigs applied for."));
      return [];
    } finally {
      setIsLoadingGigs(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadGigs = async () => {
      try {
        setIsLoadingGigs(true);
        setGigError(null);
        const response = await gigsApi.listTeenRequestedGigs();
        if (!cancelled) {
          setGigs(response);
        }
      } catch (requestError) {
        if (!cancelled) {
          setGigError(apiErrorToMessage(requestError, "Failed to load your gigs applied for."));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingGigs(false);
        }
      }
    };

    void loadGigs();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleWithdraw = async (requestId: string) => {
    setPendingRequestId(requestId);
    try {
      await gigsApi.withdrawTeenGigRequest(requestId);
      await loadTeenGigs();
      captureGigRequestWithdrawn(posthog, { request_id: requestId });
      setPendingWithdrawGig(null);
      setToast({ tone: "success", message: "Gig request withdrawn. You can find it in Past Gigs Applied For." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to withdraw your gig request.");
      setGigError(message);
      setToast({ tone: "error", message });
    } finally {
      setPendingRequestId(null);
    }
  };

  const handleOpenApplyAgain = (gig: TeenGigRequestListItemResponse) => {
    if (teenGuardianRequirement.requiresGuardian && (teenGuardianRequirement.isLoading || teenGuardianRequirement.isFetching)) {
      setToast({ tone: "info", message: "Checking your guardian setup. Try again in a second." });
      return;
    }

    if (teenGuardianRequirement.requiresGuardian && teenGuardianRequirement.requiresSetup) {
      navigate(buildGuardianSetupHref("/teen/gigs"));
      return;
    }

    setPendingReapplyGig(gig);
    setPendingReapplyRequestId(null);
    setReapplyMessage(gig.message ?? "");
    setReapplyStartTime(toDateTimeLocalInputInTimeZone(gig.proposedStartTime, gig.gigTimeZone));
    setReapplyEndTime(toDateTimeLocalInputInTimeZone(gig.proposedEndTime, gig.gigTimeZone));
    setReapplyError(null);
  };

  const loadPayoutProfileForModal = async () => {
    setIsLoadingPayoutProfile(true);
    setPayoutSetupError(null);
    try {
      const profile = await stripeApi.getTeenPayoutProfile();
      setPayoutProfile(profile);
      return profile;
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Unable to check your payout setup status.");
      setPayoutSetupError(message);
      return null;
    } finally {
      setIsLoadingPayoutProfile(false);
    }
  };

  const handleOpenPayoutSetup = (gig: TeenGigRequestListItemResponse) => {
    setPayoutSetupGig(gig);
    setPayoutProfile(null);
    setPayoutSetupError(null);
    void loadPayoutProfileForModal();
  };

  const closePayoutSetupModal = () => {
    if (isStartingPayoutOnboarding) {
      return;
    }

    setPayoutSetupGig(null);
    setPayoutProfile(null);
    setPayoutSetupError(null);
  };

  const handlePayoutSetupPrimaryAction = async () => {
    const amountLabel = payoutSetupGig ? formatGigPayment(payoutSetupGig.paymentAmountCents, payoutSetupGig.volunteer) : "this";
    const modalState = buildPayoutSetupModalState(payoutProfile, isLoadingPayoutProfile, payoutSetupError, amountLabel);

    if (modalState.actionKind === "retry") {
      await loadPayoutProfileForModal();
      return;
    }

    if (modalState.actionKind === "close") {
      closePayoutSetupModal();
      return;
    }

    if (modalState.actionKind === "settings" || !payoutProfile) {
      closePayoutSetupModal();
      navigate("/settings?section=gig-payouts");
      return;
    }

    setIsStartingPayoutOnboarding(true);
    setPayoutSetupError(null);
    try {
      const response = await stripeApi.createTeenPayoutOnboardingLink();
      setPayoutProfile(response.profile);
      window.location.assign(response.url);
    } catch (requestError) {
      setPayoutSetupError(apiErrorToMessage(requestError, "Unable to start Stripe payout setup."));
    } finally {
      setIsStartingPayoutOnboarding(false);
    }
  };

  const closeReapplyModal = () => {
    setPendingReapplyGig(null);
    setReapplyMessage("");
    setReapplyStartTime("");
    setReapplyEndTime("");
    setReapplyError(null);
  };

  const handleSubmitApplyAgain = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!pendingReapplyGig) {
      return;
    }

    const gig = pendingReapplyGig;
    if (!gig.gigId) {
      const message = "This gig is no longer available to reapply.";
      setReapplyError(message);
      setToast({ tone: "error", message });
      return;
    }

    const proposedStartTime = toZonedIsoString(reapplyStartTime, gig.gigTimeZone);
    const proposedEndTime = toZonedIsoString(reapplyEndTime, gig.gigTimeZone);
    if (!proposedStartTime) {
      setReapplyError("Proposed start time is required.");
      return;
    }
    if (!proposedEndTime) {
      setReapplyError("Proposed end time is required.");
      return;
    }

    const proposedStartDate = toMinutePrecision(new Date(proposedStartTime));
    const proposedEndDate = toMinutePrecision(new Date(proposedEndTime));
    if (proposedEndDate <= proposedStartDate) {
      setReapplyError("Proposed end time must be after start time.");
      return;
    }

    if (gig.gigScheduleType === "FIXED") {
      const availableFromDate = gig.gigStartTime ? toMinutePrecision(new Date(gig.gigStartTime)) : null;
      const availableUntilDate = gig.gigEndTime ? toMinutePrecision(new Date(gig.gigEndTime)) : null;
      if (availableFromDate && proposedStartDate < availableFromDate) {
        setReapplyError("Proposed start must be within the gig availability window.");
        return;
      }
      if (availableUntilDate && proposedStartDate > availableUntilDate) {
        setReapplyError("Proposed start must be within the gig availability window.");
        return;
      }
    }

    const trimmedMessage = reapplyMessage.trim();
    setPendingReapplyRequestId(gig.requestId);
    setReapplyError(null);
    try {
      setGigError(null);
      await gigsApi.requestAsTeen(gig.gigId, {
        message: trimmedMessage.length > 0 ? trimmedMessage : undefined,
        proposedStartTime,
        proposedEndTime,
      });
      await loadTeenGigs();
      captureGigAppliedAgain(posthog, { gig_id: gig.gigId });
      closeReapplyModal();
      setToast({
        tone: "success",
        message: !gig.volunteer && gig.paymentAmountCents !== null
          ? "Application submitted again. Finish payout setup in Settings if it still needs attention so this gig can be confirmed if you are selected."
          : "Application submitted again.",
      });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to apply for this gig again.");
      if (message.includes("Add a parent or guardian before applying for gigs")) {
        navigate(buildGuardianSetupHref("/teen/gigs"));
        return;
      }
      setReapplyError(message);
      setToast({ tone: "error", message });
    } finally {
      setPendingReapplyRequestId(null);
    }
  };

  const handleSubmitScheduleProposal = async (payload: ScheduleProposalRequest) => {
    if (!selectedConversationGig) {
      return;
    }

    const activeGig = selectedConversationGig;

    setPendingScheduleRequestId(activeGig.requestId);
    setScheduleProposalError(null);
    try {
      await gigsApi.submitTeenScheduleProposal(activeGig.requestId, payload);
      const updatedGigs = await loadTeenGigs();
      captureTeenScheduleProposalSubmitted(posthog, { request_id: activeGig.requestId });
      setSelectedConversationGig(updatedGigs.find((gig) => gig.requestId === activeGig.requestId) ?? activeGig);
      setIsScheduleProposalComposerOpen(false);
      setToast({ tone: "success", message: "New proposed time sent to the gig poster." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to send your updated proposed time.");
      setScheduleProposalError(message);
    } finally {
      setPendingScheduleRequestId(null);
    }
  };

  const handleAcknowledgeGigChange = async (gig: TeenGigRequestListItemResponse) => {
    setPendingScheduleRequestId(gig.requestId);
    setScheduleProposalError(null);
    try {
      await gigsApi.acknowledgeTeenGigChange(gig.requestId);
      const updatedGigs = await loadTeenGigs();
      setSelectedConversationGig(updatedGigs.find((request) => request.requestId === gig.requestId) ?? gig);
      setToast({ tone: "success", message: "Gig update acknowledged. Your request stays active." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to acknowledge the gig update.");
      setScheduleProposalError(message);
    } finally {
      setPendingScheduleRequestId(null);
    }
  };

  const handleConfirmSuggestedTime = async (gig: TeenGigRequestListItemResponse) => {
    setPendingScheduleRequestId(gig.requestId);
    setScheduleProposalError(null);
    try {
      await gigsApi.confirmTeenSuggestedTime(gig.requestId);
      await loadTeenGigs();
      captureTeenSuggestedTimeConfirmed(posthog, { request_id: gig.requestId, gig_title: gig.gigTitle });
      setToast({ tone: "success", message: "Suggested time confirmed." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to confirm the suggested time.");
      setGigError(message);
      setToast({ tone: "error", message });
    } finally {
      setPendingScheduleRequestId(null);
    }
  };

  const handleUnconfirmSuggestedTime = async (gig: TeenGigRequestListItemResponse) => {
    setPendingScheduleRequestId(gig.requestId);
    setScheduleProposalError(null);
    try {
      await gigsApi.unconfirmTeenSuggestedTime(gig.requestId);
      await loadTeenGigs();
      setToast({ tone: "success", message: "Suggested time unconfirmed." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to unconfirm the suggested time.");
      setGigError(message);
      setToast({ tone: "error", message });
    } finally {
      setPendingScheduleRequestId(null);
    }
  };

  const summary = buildRequestSummary(gigs);
  const reapplyTimeZone = resolveGigTimeZone(pendingReapplyGig?.gigTimeZone ?? null);
  const reapplyCurrentMinuteInput = toDateTimeLocalInputInTimeZone(toMinutePrecision(new Date()).toISOString(), reapplyTimeZone);
  const reapplyAvailabilityStartInput = toDateTimeLocalInputInTimeZone(pendingReapplyGig?.gigStartTime ?? null, reapplyTimeZone);
  const reapplyAvailabilityEndInput = toDateTimeLocalInputInTimeZone(pendingReapplyGig?.gigEndTime ?? null, reapplyTimeZone);
  const reapplyIsFlexibleGig = pendingReapplyGig?.gigScheduleType === "FLEXIBLE";
  const reapplyStartMin = reapplyIsFlexibleGig
    ? reapplyCurrentMinuteInput
    : reapplyAvailabilityStartInput || undefined;
  const reapplyStartMax = reapplyIsFlexibleGig ? undefined : reapplyAvailabilityEndInput || undefined;
  const reapplyEndMin = reapplyStartTime || reapplyStartMin;
  const normalizedSearch = search.trim().toLowerCase();
  const filteredGigs = gigs.filter((gig) => {
    const matchesSearch = normalizedSearch.length === 0
      || gig.gigTitle?.toLowerCase().includes(normalizedSearch)
      || gig.gigCategory?.toLowerCase().includes(normalizedSearch);
    if (!matchesSearch) {
      return false;
    }

    if (requestViewFilter === "ACTIVE") {
      return ACTIVE_REQUEST_STATUSES.includes(gig.status);
    }
    if (requestViewFilter === "AWAITING_CONFIRMATION") {
      return isAwaitingTeenScheduleConfirmation(gig);
    }
    if (requestViewFilter === "PAST") {
      return PAST_REQUEST_STATUSES.includes(gig.status);
    }
    return true;
  });
  const pageCount = Math.ceil(filteredGigs.length / PAGE_SIZE);
  const safePage = pageCount === 0 ? 0 : Math.min(page, pageCount - 1);
  const paginatedGigs = filteredGigs.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const hasPreviousPage = safePage > 0;
  const hasNextPage = safePage + 1 < pageCount;
  const emptyState = getEmptyStateCopy(requestViewFilter);
  const canSuggestStartTime = selectedConversationGig
    ? !selectedConversationGig.teenConfirmedPosterTimeAt
      && (selectedConversationGig.status === "POSTER_PENDING" || selectedConversationGig.requiresScheduleReproposal)
    : false;
  const payoutSetupAmountLabel = payoutSetupGig
    ? formatGigPayment(payoutSetupGig.paymentAmountCents, payoutSetupGig.volunteer)
    : "this";
  const payoutSetupModalState = buildPayoutSetupModalState(
    payoutProfile,
    isLoadingPayoutProfile,
    payoutSetupError,
    payoutSetupAmountLabel,
  );

  useEffect(() => {
    const targetRequestId = searchParams.get("requestId");
    if (!targetRequestId) {
      return;
    }

    const matchingGig = gigs.find((gig) => gig.requestId === targetRequestId);
    if (!matchingGig) {
      return;
    }

    const requestedSection = searchParams.get("section");
    const nextSection = isModalSection(requestedSection) ? requestedSection : null;
    const activeGigs = gigs.filter((gig) => ACTIVE_REQUEST_STATUSES.includes(gig.status));
    const targetIndex = activeGigs.findIndex((gig) => gig.requestId === targetRequestId);

    setRequestViewFilter("ACTIVE");
    setSearch("");
    setHighlightedRequestId(nextSection === null ? targetRequestId : null);
    if (nextSection === "details") {
      setSelectedDetailGig(matchingGig);
      setSelectedConversationGig(null);
    } else if (nextSection === "schedule") {
      setSelectedConversationGig(matchingGig);
      setSelectedDetailGig(null);
    } else {
      setSelectedDetailGig(null);
      setSelectedConversationGig(null);
    }
    if (targetIndex >= 0) {
      setPage(Math.floor(targetIndex / PAGE_SIZE));
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("requestId");
    nextParams.delete("section");
    setSearchParams(nextParams, { replace: true });
  }, [gigs, searchParams, setSearchParams]);

  useEffect(() => {
    if (!highlightedRequestId) {
      return;
    }

    const highlightedCard = document.getElementById(`teen-request-${highlightedRequestId}`);
    if (highlightedCard) {
      highlightedCard.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedRequestId(null);
    }
  }, [highlightedRequestId, paginatedGigs]);

  useEffect(() => {
    if (selectedDetailGig) {
      const refreshedGig = gigs.find((gig) => gig.requestId === selectedDetailGig.requestId) ?? null;
      setSelectedDetailGig(refreshedGig);
    }
    if (selectedConversationGig) {
      const refreshedGig = gigs.find((gig) => gig.requestId === selectedConversationGig.requestId) ?? null;
      setSelectedConversationGig(refreshedGig);
    }
    if (pendingWithdrawGig) {
      const refreshedGig = gigs.find((gig) => gig.requestId === pendingWithdrawGig.requestId) ?? null;
      setPendingWithdrawGig(refreshedGig);
    }
    if (pendingReapplyGig) {
      const refreshedGig = gigs.find((gig) => gig.requestId === pendingReapplyGig.requestId) ?? null;
      setPendingReapplyGig(refreshedGig);
    }
  }, [gigs, pendingReapplyGig, pendingWithdrawGig, selectedConversationGig, selectedDetailGig]);

  return (
    <main className="mx-auto max-w-6xl px-2 py-6 sm:px-6 sm:py-12">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <PageBackButton fallbackTo="/dashboard" label="Back to Dashboard" className="mb-4" />
      <section className="overflow-hidden rounded-[2rem] border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(163,230,53,0.24),_transparent_32%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(244,247,240,1))] shadow-sm">
        <div className="grid gap-8 px-3 py-6 sm:px-6 sm:py-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)] lg:px-8">
          <div className="lg:col-span-2">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-myteengig-text-primary sm:text-4xl">Gigs Applied For</h1>
              <p className="mt-4 text-base leading-7 text-myteengig-text-muted">
                View all the gigs that you have applied for.
              </p>
            </div>
          </div>
        </div>
      </section>

      <TeenRequestedGigsFilters
        selectedFilter={requestViewFilter}
        search={search}
        totalRequests={summary.total}
        activeRequests={summary.active}
        awaitingConfirmationRequests={summary.awaitingConfirmation}
        closedRequests={summary.closed}
        withdrawnRequests={summary.withdrawn}
        onFilterChange={(nextFilter) => {
          setRequestViewFilter(nextFilter);
          setPage(0);
        }}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
      />

      {isLoadingGigs ? <p className="mt-6 text-myteengig-text-muted">Loading your gigs applied for...</p> : null}
      {gigError ? <p className="mt-6 text-sm font-medium text-red-600">{gigError}</p> : null}

      {!isLoadingGigs && !gigError && filteredGigs.length === 0 ? (
        <section className="mt-6 rounded-[1.75rem] border border-dashed border-myteengig-border bg-white px-6 py-10 text-center">
          <p className="text-lg font-semibold text-myteengig-text-primary">{emptyState.title}</p>
          <p className="mt-2 text-sm text-myteengig-text-muted">{emptyState.body}</p>
        </section>
      ) : null}

      {!isLoadingGigs && !gigError && filteredGigs.length > 0 ? (
        <>
          <TeenRequestedGigsTable
            gigs={paginatedGigs}
            onOpenDetails={setSelectedDetailGig}
            onOpenConversation={setSelectedConversationGig}
            onOpenWithdraw={setPendingWithdrawGig}
            onApplyAgain={handleOpenApplyAgain}
            onOpenPayoutSetup={handleOpenPayoutSetup}
          />
          <Pagination
            page={safePage}
            pageSize={PAGE_SIZE}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            resultsCount={paginatedGigs.length}
            onPageChange={setPage}
          />
        </>
      ) : null}

      <Modal
        isOpen={selectedDetailGig !== null}
        onClose={() => setSelectedDetailGig(null)}
        title={selectedDetailGig?.gigTitle ?? "Gig Details"}
        panelClassName="max-w-5xl"
      >
        {selectedDetailGig ? (
          <TeenRequestedGigDetail
            gig={selectedDetailGig}
            availableSections={["details"]}
            onOpenPayoutSetup={handleOpenPayoutSetup}
          />
        ) : null}
      </Modal>

      <Modal
        isOpen={payoutSetupGig !== null}
        onClose={closePayoutSetupModal}
        title="Finish Payout Setup"
        panelClassName="max-w-xl"
      >
        {payoutSetupGig ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
              <p className="text-base font-semibold text-emerald-700">The Poster Has Selected you!</p>
              <p className="mt-2 text-sm font-semibold text-emerald-950">{payoutSetupGig.gigTitle ?? "This gig"}</p>
              <p className="mt-1 text-sm leading-6 text-emerald-900">
                Finish payout setup so the poster can fund this gig and your payout can be processed after completion.
              </p>
            </div>

            <div className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-myteengig-text-primary">{payoutSetupModalState.title}</p>
                  <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">{payoutSetupModalState.description}</p>
                </div>
                <span className="inline-flex shrink-0 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                  {payoutSetupModalState.statusLabel}
                </span>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={isStartingPayoutOnboarding}
                onClick={closePayoutSetupModal}
              >
                Close
              </Button>
              <Button
                type="button"
                disabled={isLoadingPayoutProfile || isStartingPayoutOnboarding}
                onClick={() => void handlePayoutSetupPrimaryAction()}
              >
                {isStartingPayoutOnboarding ? "Opening Stripe..." : payoutSetupModalState.actionLabel}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingWithdrawGig !== null}
        onClose={() => {
          if (pendingRequestId === null) {
            setPendingWithdrawGig(null);
          }
        }}
        title="Withdraw Gig Request?"
        panelClassName="max-w-lg"
      >
        {pendingWithdrawGig ? (
          <div className="space-y-4">
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">{pendingWithdrawGig.gigTitle ?? "This gig"}</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                This removes the request from the active decision flow. You can apply again later if the gig is still open.
              </p>
            </div>

            <p className="text-sm text-myteengig-text-muted">Are you sure you want to withdraw this request?</p>

            <div className="flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                disabled={pendingRequestId === pendingWithdrawGig.requestId}
                onClick={() => setPendingWithdrawGig(null)}
                className="w-full sm:w-auto"
              >
                Keep Request
              </Button>
              <Button
                disabled={pendingRequestId === pendingWithdrawGig.requestId}
                onClick={() => void handleWithdraw(pendingWithdrawGig.requestId)}
                className="w-full sm:w-auto"
              >
                {pendingRequestId === pendingWithdrawGig.requestId ? "Withdrawing..." : "Yes, Withdraw"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingReapplyGig !== null}
        onClose={() => {
          if (pendingReapplyRequestId === null) {
            closeReapplyModal();
          }
        }}
        title="Apply Again?"
        panelClassName="max-w-2xl"
      >
        {pendingReapplyGig ? (
          <form className="space-y-5" onSubmit={(event) => void handleSubmitApplyAgain(event)}>
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">{pendingReapplyGig.gigTitle ?? "This gig"}</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                Review your proposed time and note before submitting this request again.
              </p>
              {pendingReapplyGig.gigStartTime && pendingReapplyGig.gigEndTime ? (
                <p className="mt-3 text-sm font-medium text-myteengig-text-primary">
                  Posted availability: {formatDateTimeInGigTimeZone(pendingReapplyGig.gigStartTime, pendingReapplyGig.gigTimeZone)} to {formatDateTimeInGigTimeZone(pendingReapplyGig.gigEndTime, pendingReapplyGig.gigTimeZone)}
                </p>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="reapply-proposed-start" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                  Proposed Start
                </label>
                <input
                  id="reapply-proposed-start"
                  type="datetime-local"
                  required
                  min={reapplyStartMin}
                  max={reapplyStartMax}
                  value={reapplyStartTime}
                  onChange={(event) => {
                    setReapplyStartTime(event.target.value);
                    setReapplyError(null);
                  }}
                  className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                />
              </div>
              <div>
                <label htmlFor="reapply-proposed-end" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                  Proposed End
                </label>
                <input
                  id="reapply-proposed-end"
                  type="datetime-local"
                  required
                  min={reapplyEndMin}
                  value={reapplyEndTime}
                  onChange={(event) => {
                    setReapplyEndTime(event.target.value);
                    setReapplyError(null);
                  }}
                  className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                />
              </div>
            </div>

            <div>
              <label htmlFor="reapply-message" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                Application Note (optional)
              </label>
              <p className="mb-2 rounded-xl border border-myteengig-border bg-myteengig-background px-3 py-2 text-xs leading-relaxed text-myteengig-text-muted">
                {PRE_ACCEPTANCE_CONTACT_INFO_NOTICE}{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-myteengig-primary underline underline-offset-2 hover:text-myteengig-primary-hover"
                >
                  Review the Terms
                </a>
                .
              </p>
              <textarea
                id="reapply-message"
                maxLength={5000}
                value={reapplyMessage}
                onChange={(event) => setReapplyMessage(event.target.value)}
                className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                rows={5}
                placeholder="Anything the poster should know about you or your availability?"
              />
            </div>

            {reapplyError ? <p className="text-sm font-medium text-red-600">{reapplyError}</p> : null}

            <div className="flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                disabled={pendingReapplyRequestId === pendingReapplyGig.requestId}
                onClick={() => {
                  closeReapplyModal();
                }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={pendingReapplyRequestId === pendingReapplyGig.requestId}
                className="w-full sm:w-auto"
              >
                {pendingReapplyRequestId === pendingReapplyGig.requestId ? "Submitting..." : "Submit Application Again"}
              </Button>
            </div>
          </form>
        ) : null}
      </Modal>

      <GigRequestConversationModal
        isOpen={selectedConversationGig !== null}
        onClose={() => {
          setSelectedConversationGig(null);
          setIsScheduleProposalComposerOpen(false);
          setScheduleProposalError(null);
        }}
        title={selectedConversationGig?.gigTitle ?? "Message / Schedule"}
        role="teen"
        requestId={selectedConversationGig?.requestId ?? ""}
        participantLabel="gig poster"
        perspective="teen"
        proposals={selectedConversationGig?.scheduleProposals}
        timeZone={selectedConversationGig?.gigTimeZone}
        summary={selectedConversationGig ? (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-myteengig-text-primary">{selectedConversationGig.gigTitle ?? "This gig"}</p>
            <p className="text-sm text-myteengig-text-muted">
              You can message the poster as soon as you apply. If timing needs to change, handle that here too.
            </p>
            {selectedConversationGig.requiresScheduleReproposal ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-semibold">Your requested time needs a new start time.</p>
                <p className="mt-1 text-amber-800">
                  {selectedConversationGig.latestGigChangeSummary?.trim()
                    || "The poster updated this gig and your requested time no longer fits the current gig window."}
                </p>
              </div>
            ) : null}
            {selectedConversationGig.requiresGigChangeAcknowledgment ? (
              <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                <p className="font-semibold">Review the updated gig details.</p>
                <p className="mt-1 text-sky-800">
                  {selectedConversationGig.latestGigChangeSummary?.trim()
                    || "The poster updated this gig. Review the change and confirm you still want this request to stay active."}
                </p>
              </div>
            ) : null}
            {selectedConversationGig.teenConfirmedPosterTimeAt ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                You confirmed the latest time suggestion. The poster can now decide whether to accept you for this gig.
              </div>
            ) : null}
            <div className="flex flex-col gap-3 sm:flex-row">
              {selectedConversationGig.requiresGigChangeAcknowledgment && !selectedConversationGig.requiresScheduleReproposal ? (
                <Button
                  variant="outline"
                  disabled={pendingScheduleRequestId === selectedConversationGig.requestId}
                  onClick={() => void handleAcknowledgeGigChange(selectedConversationGig)}
                >
                  {pendingScheduleRequestId === selectedConversationGig.requestId ? "Saving..." : "Acknowledge and Continue"}
                </Button>
              ) : null}
              {selectedConversationGig.status === "POSTER_PENDING"
                && selectedConversationGig.latestProposalByRole === "POSTER"
                && !selectedConversationGig.teenConfirmedPosterTimeAt
                && selectedConversationGig.proposedStartTime
                && selectedConversationGig.proposedEndTime ? (
                  <Button
                    disabled={pendingScheduleRequestId === selectedConversationGig.requestId}
                    onClick={() => void handleConfirmSuggestedTime(selectedConversationGig)}
                  >
                    {pendingScheduleRequestId === selectedConversationGig.requestId ? "Confirming..." : "Confirm Suggested Time"}
                  </Button>
                ) : null}
              {selectedConversationGig.status === "POSTER_PENDING"
                && selectedConversationGig.latestProposalByRole === "POSTER"
                && selectedConversationGig.teenConfirmedPosterTimeAt ? (
                  <Button
                    variant="outline"
                    disabled={pendingScheduleRequestId === selectedConversationGig.requestId}
                    onClick={() => void handleUnconfirmSuggestedTime(selectedConversationGig)}
                  >
                    {pendingScheduleRequestId === selectedConversationGig.requestId ? "Updating..." : "Unconfirm Suggested Time"}
                  </Button>
                ) : null}
            </div>
          </div>
        ) : null}
        scheduleProposalContent={canSuggestStartTime
          && isScheduleProposalComposerOpen ? (
            <ScheduleProposalForm
              subtitle="Pick a new start time and MyTeenGig will keep the same gig length for the updated proposal."
              initialStartTime={selectedConversationGig!.proposedStartTime ?? null}
              initialEndTime={selectedConversationGig!.proposedEndTime ?? null}
              availabilityStartTime={selectedConversationGig!.gigStartTime ?? null}
              availabilityEndTime={selectedConversationGig!.gigEndTime ?? null}
              timeZone={selectedConversationGig!.gigTimeZone}
              submitError={scheduleProposalError}
              isSubmitting={pendingScheduleRequestId === selectedConversationGig!.requestId}
              onCancel={() => {
                setIsScheduleProposalComposerOpen(false);
                setScheduleProposalError(null);
              }}
              onSubmit={handleSubmitScheduleProposal}
            />
          ) : null}
        composerSecondaryAction={canSuggestStartTime ? (
          <Button
            variant="outline"
            disabled={pendingScheduleRequestId === selectedConversationGig!.requestId}
            onClick={() => {
              setIsScheduleProposalComposerOpen((current) => !current);
              setScheduleProposalError(null);
            }}
          >
            {pendingScheduleRequestId === selectedConversationGig!.requestId
              ? "Sending..."
              : isScheduleProposalComposerOpen
                ? "Hide Start Time Picker"
                : "Suggest New Start Time"}
          </Button>
        ) : null}
        emptyThreadMessage="No messages or timing updates yet."
        messagesDescription="Messages and timing changes all stay in this same request thread."
      />
    </main>
  );
};

export default TeenGigsPage;
